import { useState } from "react";
import { FeatureKey } from "@shared/schemas/feature-key.schemas";
import { EventIdPublicOrSlug } from "@shared/schemas/branded-id.schemas";
import {
  useFeatureConfigs,
  type UseFeatureConfigsOutput,
} from "@frontend/shared/features/feature-config/application/hooks/use-feature-configs";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";
import { useRequiredEventRouteContext } from "@/shared/presentation/hooks/useEventRouteContext";

/**
 * `confirm` before anything is sent, because the file arrives in another app:
 * a member who had not asked for it would have no idea why WhatsApp buzzed.
 * `needsInboundMessage` is an outcome rather than a failure — the request
 * worked, and WhatsApp will not carry a file to someone who has not written to
 * Porter in 24h.
 *
 * `queued`, not `sent`: Porter delivers the file on its own turn, so the reply
 * this state is built from reports that the export was accepted and nothing
 * more. The copy already said so ("On its way"); the name follows it.
 */
export type DirectoryExportModalState =
  | "closed"
  | "confirm"
  | "sending"
  | "queued"
  | "needsInboundMessage"
  | "error";

/**
 * `enabled` is the admin's global switch and `config.active` the event-scoped
 * one; they move independently, so a flag is only live when both say so.
 */
function isFeatureActive(
  features: UseFeatureConfigsOutput,
  featureKey: FeatureKey,
): boolean {
  return (
    features.isFeatureEnabled(featureKey) === true &&
    features.getFeatureConfig(featureKey)?.config.active === true
  );
}

interface DirectoryExportSectionControllerReturn {
  readonly state: {
    readonly isVisible: boolean;
    readonly modalState: DirectoryExportModalState;
    /**
     * Drives the description's column list: the file omits `whyConnect` when
     * reasons are unavailable, so copy naming that column would promise
     * something the file does not carry.
     */
    readonly includeWhyConnect: boolean;
  };
  readonly actions: {
    readonly openConfirmation: () => void;
    readonly closeModal: () => void;
    readonly sendExport: () => void;
  };
}

export function useDirectoryExportSectionController(): DirectoryExportSectionControllerReturn {
  const eventContext = useRequiredEventRouteContext();
  const eventIdPublic = eventContext.eventIdPublic;

  const [modalState, setModalState] =
    useState<DirectoryExportModalState>("closed");

  const features = useFeatureConfigs({
    context: { eventIdPublic },
    enabled: true,
  });

  // Browsing is gated alongside export because the file is built by reading the
  // directory, so an export offered without it could not be generated.
  const isVisible =
    isFeatureActive(features, FeatureKey.DIRECTORY_EXPORT) &&
    isFeatureActive(features, FeatureKey.DIRECTORY_BROWSING);

  const isInactiveForIsolated =
    features.getFeatureConfig(FeatureKey.AI_RECOMMENDATIONS)?.config
      .inactiveForIsolated === true &&
    eventContext.currentParticipant.isolated === true;
  const includeWhyConnect =
    isFeatureActive(features, FeatureKey.AI_RECOMMENDATIONS) &&
    isInactiveForIsolated === false;

  const eventActions = useUserActions().useForEvent({
    eventIdPublicOrSlug: EventIdPublicOrSlug.unsafeCreate(
      eventContext.eventIdPublicOrSlug,
    ),
  });
  const createExport = eventActions.useCreateDirectoryExport();

  const sendExport = (): void => {
    setModalState("sending");
    createExport.mutate(
      { eventIdPublic },
      {
        onSuccess: (result) => {
          setModalState(
            result.status === "queued" ? "queued" : "needsInboundMessage",
          );
        },
        onError: () => {
          setModalState("error");
        },
      },
    );
  };

  return {
    state: { isVisible, modalState, includeWhyConnect },
    actions: {
      openConfirmation: (): void => {
        setModalState("confirm");
      },
      closeModal: (): void => {
        setModalState("closed");
      },
      sendExport,
    },
  };
}
